<template>
    <div class="payment-entry-interface" @keydown="keydown">
        <div class="raised low p-25 text-center" style="font-size:1.5em" v-if="loading_methods">
            Loading...
        </div>

        <div class="raised low p-25 text-center" v-else-if="methods_load_error">
            {{ methods_load_error }}
        </div>

        <div class="row full-height-cols" v-else-if="!methods_load_error">
            <!-- Payment Information -->
            <div class="col-xs-12 col-sm-5 col-md-6">
                <div class="form-card p-b-5" style="height: 100%;">
                    <div class="row p-b-0">
                        <div class="col-sm-6">
                            <div class="form-group">
                                <label class="form-label text-nowrap">Payment Method</label>
                                <select autofocus
                                        class="form-control navigate-with-keyboard"
                                        :class="{'empty-select': selected_method == null }"
                                        v-model="selected_method"
                                >
                                    <option :value="null">Payment Method</option>
                                    <option v-for="method in available_methods" :value="method">{{ method.name }}</option>
                                </select>
                            </div>
                        </div>
                        <div class="col-sm-6">
                            <div class="form-group">
                                <label class="form-label text-nowrap">Check Number</label>
                                <input type="text"
                                       class="form-control navigate-with-keyboard"
                                       v-model="check_nbr"
                                       ref="check_nbr"
                                       :disabled="!show_check_nbr"
                                >
                            </div>
                        </div>
                    </div>

                    <div class="row p-b-0">
                        <div class="col-sm-6">
                            <div class="form-group">
                                <label class="form-label text-nowrap">
                                    Account
                                    <account-picker :label="null" type="text" ref="account_picker" @select="accountSelected"></account-picker>
                                    <i class="fa fa-search m-l-25 text-primary" title="Find account from invoice or report number" @click="showAccountPicker"></i>
                                </label>
                                <input type="number" min="1"
                                       class="form-control navigate-with-keyboard"
                                       :class="{ invalid: account_nbr && !account_is_valid }"
                                       v-model="account_nbr"
                                       @blur="getPayables"
                                       ref="account_nbr"
                                >
                            </div>
                        </div>
                        <div class="col-sm-6">
                            <div class="form-group">
                                <label class="form-label text-nowrap">Payment Amount</label>
                                <input type="number" min="0.01" step="0.01"
                                       class="form-control navigate-with-keyboard"
                                       v-model="amount"
                                       :disabled="applying_account_credit"
                                       @blur="formatAmount"
                                >
                            </div>
                        </div>
                    </div>

                    <div class="row p-b-0">
                        <div class="col-xs-12">
                            <div class="form-group">
                                <label class="form-label text-nowrap">Memo</label>
                                <input type="text"
                                       class="form-control navigate-with-keyboard"
                                       v-model="memo"
                                >
                            </div>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-xs-12 text-right">
                            <button type="button"
                                    class="btn btn-primary btn-lg navigate-with-keyboard"
                                    ref="submit_button"
                                    :disabled="!ready_to_submit || processingPayment"
                                    @click="applyPayment"
                            >
                                <i class="fa fa-check m-r-5"></i>
                                {{ processingPayment ? 'Processing...' : 'Apply Payment' }}
                            </button>
                        </div>
                    </div>

                </div>
            </div>

            <!-- Selected Account Info -->
            <div class="col-xs-12 col-sm-7 col-md-6">
                <div class="hidden visible-xs mt-10">&nbsp;</div>

                <div class="form-card p-t-10 p-b-5" style="height: 100%;">
                    <div v-show="current_account.account">
                        <div class="p-b-25 text-center">
                            <div style="font-size: 200%;">{{ current_account.account }}</div>
                            <div>
                                <span>{{ current_account.name }}</span>
                                <span class="text-bold p-l-5 p-r-5" v-if="current_account.name && current_account.city_state_zip">|</span>
                                <span>{{ current_account.city_state_zip }}</span>
                            </div>
                        </div>

                        <div class="row text-center text-dark-blue text-bold">
                            <div class="col-xs-6 p-r-25">
                                Current
                            </div>
                            <div class="col-xs-6 p-r-25">
                                After Payment
                            </div>
                        </div>

                        <div class="row text-center">
                            <div class="col-xs-6">
                                <div style="font-size: 200%;">{{ formatPrice(current_account.balance) }}</div>
                                <div>Balance</div>
                            </div>
                            <div class="col-xs-6">
                                <div style="font-size: 200%;">{{ formatPrice(balance_after_payment) }}</div>
                                <div>Balance</div>
                            </div>
                        </div>

                        <div class="row text-center">
                            <div class="col-xs-6">
                                <div v-show="current_account.credit > 0">
                                    <div style="font-size: 200%;">{{ formatPrice(current_account.credit) }}</div>
                                    <div>Credit</div>
                                </div>
                            </div>
                            <div class="col-xs-6">
                                <div v-show="credit_after_payment > 0">
                                    <div style="font-size: 200%;">{{ formatPrice(credit_after_payment) }}</div>
                                    <div>Credit</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Bottom Tabs -->
        <ul class="nav nav-tabs m-t-25 p-r-5">
            <li class="remaining-amount pull-left" v-show="amount > 0">
                <a href="#" tabindex="-1">
                    <span>Remaining:</span>
                    <span class="p-l-10 p-r-10">{{ formatPrice(remaining_amount) }}</span>
                    <span class="text-danger" v-show="remaining_amount > 0">(to be applied as account credit)</span>
                </a>
            </li>
            <li class="pull-right" :class="{ active: viewing == 'history', disabled: !current_account.account }" >
                <a href="#" tabindex="-1" data-view="history" @click="changeView">History</a>
            </li>
            <li class="pull-right" :class="{ active: viewing == 'payables' }">
                <a href="#" tabindex="-1" data-view="payables" @click="changeView">Payables</a>
            </li>
        </ul>

        <transition-group name="fade" mode="out-in">
            <!-- Payables -->
            <div class="p-25" v-show="viewing == 'payables'" key="payables">
                <div class="text-center" style="font-size:1.5em" v-if="loading_payables">
                    Loading...
                </div>

                <div class="text-danger" v-else-if="payables_error">
                    {{ payables_error }}
                </div>

                <div v-else-if="account_nbr && payables.length == 0">
                    No payable items were found
                </div>

                <div v-else-if="payables.length > 0">
                    <table class="table table-sm table-hover raised low">
                        <thead class="raised low">
                        <tr>
                            <th scope="col" class="no-stretch"></th>
                            <th scope="col" class="text-nowrap">Type</th>
                            <th scope="col" class="text-nowrap">ID</th>
                            <th scope="col" class="text-nowrap">Account</th>
                            <th scope="col" class="text-nowrap">Bill To</th>
                            <th scope="col" class="text-nowrap">Date</th>
                            <th scope="col" class="text-nowrap">Due Date</th>
                            <th scope="col" class="text-nowrap no-stretch text-right">Amount</th>
                            <th scope="col" class="text-nowrap no-stretch text-right">Balance</th>
                            <th scope="col" class="text-nowrap no-stretch text-right">Remaining</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="payable in payables" @click="toggleSelected(payable)">
                            <td class="text-nowrap no-stretch" style="zoom: 1.3">
                                <i class="fa fa-check-square-o" v-if="payable.selected"></i>
                                <i class="fa fa-square-o" v-else></i>
                            </td>
                            <td class="text-nowrap text-capitalize">{{ payable.type }}</td>
                            <td class="text-nowrap">{{ payable.id }}</td>
                            <td class="text-nowrap">{{ payable.account }}</td>
                            <td class="text-nowrap">{{ payable.billing_account }}</td>
                            <td class="text-nowrap">{{ payable.date }}</td>
                            <td class="text-nowrap">{{ payable.due_date }}</td>
                            <td class="text-nowrap text-right no-stretch">{{ formatPrice(payable.amount) }}</td>
                            <td class="text-nowrap text-right no-stretch">{{ formatPrice(payable.balance) }}</td>
                            <td class="text-nowrap text-right no-stretch">{{ payable.remaining > 0 ? formatPrice(payable.remaining) : '' }}</td>
                        </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- History -->
            <div class="p-25" v-show="viewing == 'history'" key="history">
                <div class="p-25 text-center" style="font-size:1.5em" v-if="loading_history">
                    Loading...
                </div>

                <div v-else-if="account_nbr && payment_history.length == 0">
                    No payment history was found
                </div>

                <div v-else-if="payment_history.length > 0">
                    <table class="table table-sm table-hover raised low">
                        <thead class="raised low">
                        <tr>
                            <th scope="col" class="no-stretch"></th>
                            <th scope="col" class="text-nowrap">ID</th>
                            <th scope="col" class="text-nowrap">Method</th>
                            <th scope="col" class="text-nowrap">Entered By</th>
                            <th scope="col" class="text-nowrap">Memo</th>
                            <th scope="col" class="text-nowrap">Timestamp</th>
                            <th scope="col" class="text-nowrap no-stretch text-right">Total</th>
                            <th></th>
                        </tr>
                        </thead>
                        <tbody>
                        <template v-for="history in payment_history">
                            <tr @click="toggleItems(history)">
                                <td class="expander">
                                    <i class="fa fa-caret-down" v-if="history.expanded"></i>
                                    <i class="fa fa-caret-right" v-else></i>
                                </td>
                                <td class="text-nowrap">{{ history.payment_id }}</td>
                                <td class="text-nowrap">
                                    {{ history.payment_method }}
                                    <span v-if="history.check_nbr">#{{ history.check_nbr }}</span>
                                </td>
                                <td class="text-nowrap" :title="history.user.email">{{ history.user.full_nm }}</td>
                                <td class="text-nowrap">{{ history.note }}</td>
                                <td class="text-nowrap">{{ history.created_at }}</td>
                                <td class="text-nowrap text-right no-stretch">{{ formatPrice(history.payment_amt) }}</td>
                                <td class="text-right" @click.stop="">
                                    <a :href="receiptUrl(history)" target="mwl_payment_receipt" class="btn btn-sm btn-primary" v-if="receiptUrl(history)">
                                        <i class="fa fa-file-pdf-o"></i>
                                        Receipt
                                    </a>
                                </td>
                            </tr>
                            <tr v-show="history.expanded">
                                <td class="border-top-0"></td>
                                <td class="border-top-0" colspan="7">
                                    <table class="table table-sm">
                                        <thead class="raised low">
                                        <tr>
                                            <th scope="col" class="text-nowrap">Account</th>
                                            <th scope="col" class="text-nowrap">Type</th>
                                            <th scope="col" class="text-nowrap">Item ID</th>
                                            <th scope="col" class="text-nowrap">Amount</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="item in history.items">
                                                <td class="text-nowrap">{{ item.account_nbr }}</td>
                                                <td class="text-nowrap">{{ item.item_type }}</td>
                                                <td class="text-nowrap">{{ item.item_id }}</td>
                                                <td class="text-nowrap">{{ formatPrice(item.amount_paid) }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </td>
                            </tr>
                        </template>
                        </tbody>
                    </table>
                </div>
            </div>
        </transition-group>
    </div>
</template>

<style lang="scss">
    @import '../../sass/_global_variables.scss';

    .payment-entry-interface {

        input.invalid, select.invalid, textarea.invalid {
            border-color: red;
        }

        .empty-select, input::placeholder {
            color: #aaaaaa;
        }

        .text-dark-blue {
            color: $dark-blue;
        }

        option:not([value=""]) {
            color: #2e2e2e;
        }

        .expander {
            i.fa {
                zoom: 1.5;
                width: 9px;
                vertical-align: middle;
            }
        }
        .row.full-height-cols {
            display: flex;
            flex-wrap: wrap;

            > [class*='col-'] {
                display: flex;
                flex-direction: column;
            }
        }

        .nav-tabs {
            border-color: $dark-blue;

            li a {
                border-color: $dark-blue;
            }

            li.active a {
                background-color: $dark-blue;
                color: #ffffff;

                &:hover, &:focus {
                    border-color: transparent;
                }
            }

            .remaining-amount a {
                border-color: transparent;
                cursor: default;
                color: $body-text;
                
                &:hover, &:focus {
                    background-color: transparent
                }
            }
        }

        .table {
            max-height: 100%;
            overflow: hidden;

            thead{
                th {
                    background-color: $dark-gray-blue;
                    color: #ffffff;
                    font-weight: bold;
                    border-bottom: none;
                }
            }

            .table {
                background-color: transparent;

                thead th {
                    background-color: $gray-blue;
                }

                tbody {
                    border: 2px solid $gray-blue;

                    tr:first-child td {
                        border-top: none;
                    }
                }
            }

            .border-top-0 {
                border-top: none !important;
            }

            .no-stretch {
                width: 1px;
            }

            .fa {
                width: 14px;
            }
        }
    }
</style>

<script>
    export default{
        props: {

        },

        data () {
            return {
                viewing: 'payables',
                loading_methods: true,
                loading_payables: false,
                loading_history: false,
                payables_error: null,
                methods_load_error: null,
                available_methods: [],
                selected_method: null,
                account_nbr: "",
                check_nbr: "",
                amount: "",
                payables: [],
                payment_history: [],
                accounts: [],
                account_changed: false,
                amount_changed: false,
                memo: "",
                payment_methods: [],
                processingPayment: false
            }
        },
        watch: {
            account_nbr () {
                this.account_changed = true;
                this.payment_history = [];
                this.accounts = [];
            } ,
            amount () {
                this.amount_changed = true;
            },
            viewing () {
                if (this.viewing == 'history' && this.payment_history.length == 0) {
                    this.getPaymentHistory()
                }
            },
            selected_method () {
                if (!this.show_check_nbr) {
                    this.check_nbr = '';
                }
            },
            applying_account_credit () {
                if (this.applying_account_credit && this.current_account && this.current_account.credit) {
                    this.amount = this.current_account.credit;
                }
                else {
                    this.amount = '';
                }

                this.initialItemSelection(true)
            },
            current_account () {
                if (this.applying_account_credit) {
                    this.amount = this.current_account.credit;
                    this.initialItemSelection()
                }
            }
        },
        computed: {
            show_check_nbr () {
                let method_ids = [1,3]
                return this.selected_method ? method_ids.includes(this.selected_method.id) : false
            },
            account_is_valid () {
                return this.account_nbr.match(/^\d+$/) !== null
            },
            total_balance () {
                return this.payables.map(item => item.balance).reduce((total, balance) => total + parseFloat(balance), 0).toFixed(2)
            },
            formatted_accounts () {
                return this.accounts.map(account => {
                    account.city_state_zip = account.city
                    if (account.city_state_zip && account.state) account.city_state_zip += ', '
                    account.city_state_zip += account.state
                    if (account.city_state_zip && account.zip) account.city_state_zip += ' ' + account.zip
                    return account
                })
            },
            current_account () {
                return this.formatted_accounts.length == 1 ? this.formatted_accounts[0] : {}
            },
            selected_items () {
                return this.payables.filter(item => item.selected)
            },
            paid_items () {
                return this.selected_items.map(item => {
                    return {
                        id: item.id,
                        type: item.type,
                        account_nbr: this.account_nbr,
                        description: item.type.charAt(0).toUpperCase() + item.type.slice(1) + ' ' + item.id,
                        amount: (item.balance/1 - item.remaining/1).toFixed(2)
                    }
                })
            },
            selected_items_total () {
                return this.selected_items.map(item => item.balance).reduce((total, balance) => total + parseFloat(balance), 0).toFixed(2)
            },
            selected_items_remaining () {
                return this.selected_items.filter(item => item.remaining > 0).map(item => item.remaining).reduce((total, remaining) => total + parseFloat(remaining), 0).toFixed(2)
            },
            remaining_amount () {
                return ((this.amount/1) - (this.selected_items_total/1) + (this.selected_items_remaining/1)).toFixed(2)
            },
            balance_after_payment () {
                let bal = this.current_account.balance/1 - this.selected_items_total/1 + this.selected_items_remaining/1;
                return (bal > 0) ? bal : '0';
            },
            credit_after_payment () {
                let credit = this.current_account.credit/1;
                if (this.applying_account_credit) {
                    credit -= this.selected_items_total/1
                }
                else {
                    credit += this.remaining_amount/1
                }
                return (credit > 0) ? credit : '0';
            },
            ready_to_submit () {
                if (!this.selected_method || !this.current_account.account || !this.amount) {
                    return false;
                }

                if (this.show_check_nbr && !this.check_nbr) {
                    return false;
                }

                if (this.applying_account_credit && this.selected_items.length == 0) {
                    return false;
                }

                return true;
            },
            applying_account_credit () {
                return this.selected_method && this.selected_method.id == 16
            }
        },

        methods: {
            changeView (event) {
                let el = event.target;
                let view = el.getAttribute('data-view');

                if (view != 'history' || this.current_account.account) {
                    this.viewing = view;
                }
            },
            getPaymentMethods () {
                this.loading_methods = true

                this.$http.get('/admin/payments/methods').then(response => {
                    this.loading_methods = false
                    this.available_methods = []

                    if (response.data) {
                        this.available_methods = response.data
                    }
                    else {
                        this.available_methods_load_error = "Unable to retrieve payment methods"
                    }
                })
                .catch(() => {
                    this.available_methods_load_error = "Unable to retrieve payment methods"
                })
            },
            keydown (event) {
                const el = event.target;
                const keyCode = event.keyCode ? event.keyCode : event.which;

                if ([13, 38, 40].indexOf(keyCode) === -1) {
                    return;
                }

                event.preventDefault();
                let elements = Array.from(document.querySelectorAll(".navigate-with-keyboard:not([disabled])"));
                let index = -1;

                switch (keyCode) {
                    case 13:    // enter
                        if (el.tagName.toLowerCase() === 'button') {
                            this.applyPayment();
                            return;
                        }
                    case 40:    // down arrow
                        index = elements.indexOf(event.target) + 1
                        break;
                    case 38:    // up arrow
                        index = elements.indexOf(event.target) - 1
                        break;
                }

                if (_.has(elements,index)) {
                    elements[index].focus()
                }
            },
            getPayables () {
                if (!this.account_changed) return

                this.account_changed = false;
                this.payables = []
                this.payables_error = null

                if (this.account_nbr && this.account_is_valid) {
                    this.loading_payables = true
                    this.viewing = 'payables'

                    this.$http.get('/admin/payments/payables', {
                        params: {
                            account_nbrs: [this.account_nbr]
                        }
                    }).then(response => {
                        if (_.has(response, ['data','items'])) {
                            this.payables = response.data.items

                            this.initialItemSelection()
                        }

                        if (_.has(response, ['data','accounts'])) {
                            this.accounts = response.data.accounts
                        }

                        this.loading_payables = false
                    })
                    .catch(error => {
                        this.loading_payables = false
                        this.payables_error = this.account_nbr + " does not appear to be a valid account"
                    })
                }
            },
            getPaymentHistory () {
                if (this.payment_history.length > 0) return;

                this.loading_history = true;

                this.$http.get('/admin/payments/history', {
                    params: {
                        account_nbr: this.account_nbr
                    }
                })
                    .then(response => {
                        if (response.data) {
                            this.payment_history = response.data;
                        }

                        this.loading_history = false;
                    })
            },
            formatPrice(value) {
                if (!value) return '';
                let val = (value/1).toFixed(2)
                return '$' + val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            },
            formatAmount() {
                if (!this.amount_changed) return

                let amt = (this.amount/1).toFixed(2)
                this.amount = amt > 0 ? amt : ''
                this.amount_changed = false

                this.initialItemSelection(true)
            },
            toggleSelected(item) {
                let amt = this.amount ? this.amount/1 : 0
                let available = amt - this.selected_items_total

                if (item.selected || available > 0) {
                    Vue.set(item, 'selected', !item.selected)
                }

                if (item.selected) {
                    let balance = item.balance/1
                    let remaining = available >= balance ? 0 : balance - available

                    Vue.set(item, 'remaining', remaining.toFixed(2))
                }
                else {
                    Vue.set(item, 'remaining', null)
                }
            },
            initialItemSelection (forced=false) {
                if (this.selected_items.length > 0 && !forced) return

                this.selected_items.forEach(item => {
                    this.toggleSelected(item)
                })

                for(let i=0; i < this.payables.length && this.remaining_amount > 0; i++) {
                    this.toggleSelected(this.payables[i])
                }
            },
            applyPayment () {
                this.processingPayment = true;

                let acccount_credit = [];

                if (this.remaining_amount > 0 && !this.applying_account_credit) {
                    acccount_credit.push({
                        id: -1 * this.account_nbr,
                        type: 'account credit',
                        description: 'Credited to account',
                        amount: this.remaining_amount,
                        account_nbr: this.account_nbr
                    })
                }

                this.$http.post('/admin/payments', {
                    payment: {
                        type: this.selected_method.name,
                        check_nbr: this.check_nbr,
                        amount: this.applying_account_credit ? (this.selected_items_total/1 - this.selected_items_remaining/1).toFixed(2) : this.amount,
                        memo: this.memo,
                        items: this.paid_items.concat(acccount_credit)
                    }
                })
                    .then(response => {
                        if (response.data) {
//                            let receipt_file_id = response.data;
                        }

                        this.processingComplete();
                    })
                    .catch(error => {
                        swal({
                            title: "Error!",
                            text: "There was an error saving that payment.",
                            type: "error"
                        });

                        this.processingComplete();
                    })
            },
            processingComplete () {
                this.account_nbr = "";
                this.check_nbr = "";
                this.amount = "";
                this.payables = [];
                this.payment_history = [];
                this.accounts = [];
                this.memo = "";
                this.processingPayment = false;

                if (this.show_check_nbr) {
                    this.$refs.check_nbr.focus();
                }
                else {
                    this.$refs.account_nbr.focus();
                }
            },
            toggleItems (payment) {
                Vue.set(payment, 'expanded', !payment.expanded);
            },
            showAccountPicker () {
                this.$refs.account_picker.selectedAccounts = [];
                this.$refs.account_picker.showSelector();
            },
            accountSelected (account) {
                this.account_nbr = account.account_nbr.toString();
                this.account_changed = true;
                this.getPayables();
            },
            receiptUrl (payment) {
                if (payment.mwl_receipt_file_id && payment.items.length > 0) {
                    let account_nbr = payment.items[0].account_nbr;
                    return '/documents/' + account_nbr + '/' + payment.mwl_receipt_file_id;
                }

                return null;
            }
        },

        mounted () {
            this.getPaymentMethods()
        }
    }
</script>


// WEBPACK FOOTER //
// payments-entry-interface.vue?74023c74